// Types
import type { IUpsertModel } from '~/misc/resource/types';

// Composables
import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

// TODO: adjust properly on https://addintel.atlassian.net/browse/RC-255
export const useUpsertResource = <
  PayloadType extends { id?: number },
  ErrorType,
  ResponseType = PayloadType
>(
  model: IUpsertModel<PayloadType, ResponseType>,
  defaultResource: Partial<PayloadType>,
  resource: PayloadType | null = null
) => {
  const { notifySuccess, notifyFailure } = useNotifyResource();

  watch(
    () => resource,
    (value: PayloadType | null) => setupForm(value)
  );

  const setupForm = (resource: PayloadType | null = null) => {
    errors.value = {} as ErrorType;
    payload.value = cloneResource(resource);
  };

  const cloneResource = (resource: PayloadType | null = null): PayloadType => {
    return JSON.parse(JSON.stringify(resource || defaultResource));
  };

  const payload = ref(cloneResource(resource)) as Ref<PayloadType>;
  const errors = ref({}) as Ref<ErrorType>;
  const isLoading: Ref<boolean> = ref<boolean>(false);

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const submit = (): Promise<ResponseType> => {
    return new Promise<ResponseType>((resolve, reject) => {
      setIsLoading(true);

      (payload.value?.id
        ? model.update(payload.value.id, payload.value)
        : model.store(payload.value)
      )
        .then((response: ResponseType) => {
          setupForm(payload.value.id ? payload.value : null);
          notifySuccess();
          resolve(response);
        })
        .catch((e) => {
          // TODO: encapsulate and reuse
          if (e?.response?.data?.errors) {
            errors.value = e.response.data.errors;
          } else if (e?.response?.data.message) {
            notifyFailure(e?.response?.data.message);
          }

          reject(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  };

  return { payload, errors, isLoading, submit };
};
